"use client";

import { useEffect, useState } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import { buildCommunitySearchHref } from "@/lib/community/search-params";
import { Section } from "@/components/ui/section";
import { Container } from "@/components/ui/container";
import { CommunityHero } from "./CommunityHero";
import { CategoryStoriesRow } from "./category-stories-row";
import { CollectionCarousel } from "./collection-carousel";
import { CommunityFilterBar } from "./CommunityFilterBar";
import { RegionFilterChips } from "./RegionFilterChips";
import { CountryFilterChips, type CountryCode } from "./CountryFilterChips";
import { HighlightedSuppliersSection } from "./HighlightedSuppliersSection";
import type { QuickFilterValue } from "./QuickFilterChips";
import type { RegionCode } from "@/lib/community/constants";
import type {
  PublicCommunityBrowseData,
} from "@/lib/community/types";

const VALID_QUICK_FILTERS: QuickFilterValue[] = [
  "newest",
  "trending",
  "popularity",
];

interface CommunityPageProps {
  browse: PublicCommunityBrowseData;
}

export function CommunityPage({ browse }: CommunityPageProps) {
  const router = useRouter();
  const searchParams = useSearchParams();

  const searchQuery = searchParams.get("q") ?? "";
  const [searchInput, setSearchInput] = useState(searchQuery);

  const rawQuickFilter = searchParams.get("sort") ?? searchParams.get("quick");
  const quickFilter: QuickFilterValue = VALID_QUICK_FILTERS.includes(
    rawQuickFilter as QuickFilterValue,
  )
    ? (rawQuickFilter as QuickFilterValue)
    : null;

  const selectedRegion = searchParams.get("region") as RegionCode | null;
  const selectedCountry = searchParams.get("country") as CountryCode | null;
  const selectedCategory = searchParams.get("category");
  const selectedCollection = searchParams.get("collection");

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Restore the input draft when browser navigation changes the URL query.
    setSearchInput(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    let scrollTimers: number[] = [];

    const scheduleCommunitySectionScroll = () => {
      const sectionId = window.location.hash.slice(1);
      if (sectionId !== "categories" && sectionId !== "collections") return;

      scrollTimers.forEach(window.clearTimeout);
      scrollTimers = [0, 250, 1000].map((delay) =>
        window.setTimeout(() => {
          document.getElementById(sectionId)?.scrollIntoView({ block: "start" });
        }, delay),
      );
    };

    scheduleCommunitySectionScroll();
    window.addEventListener("hashchange", scheduleCommunitySectionScroll);
    window.addEventListener("load", scheduleCommunitySectionScroll);
    return () => {
      scrollTimers.forEach(window.clearTimeout);
      window.removeEventListener("hashchange", scheduleCommunitySectionScroll);
      window.removeEventListener("load", scheduleCommunitySectionScroll);
    };
  }, []);

  // Home search box: navigate to /community/search once typing pauses;
  // cleared input stays on the home page.
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (!trimmed || trimmed === searchQuery) return;
    const timer = setTimeout(() => {
      router.push(`/community/search?q=${encodeURIComponent(trimmed)}`);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, searchQuery, router]);

  const navigateToSearch = (updates: Record<string, string | null>) => {
    router.push(
      buildCommunitySearchHref(
        "/community/search",
        searchParams.toString(),
        updates,
      ),
    );
  };

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
  };

  const handleQuickFilterChange = (value: QuickFilterValue) => {
    if (value === null) {
      navigateToSearch({ quick: null, sort: "default" });
    } else {
      navigateToSearch({ quick: null, sort: value });
    }
  };

  const handleBasedInChange = (
    type: "region" | "country" | null,
    code: RegionCode | CountryCode | null,
  ) => {
    if (type === "region") {
      navigateToSearch({ region: code, country: null });
    } else if (type === "country") {
      navigateToSearch({ country: code, region: null });
    } else {
      navigateToSearch({ region: null, country: null });
    }
  };

  const handleRegionChange = (code: RegionCode | null) => {
    navigateToSearch({ region: code, country: null });
  };

  const handleCountryChange = (code: CountryCode | null) => {
    navigateToSearch({ country: code, region: null });
  };

  const handleCategoryChange = (id: string | null) => {
    navigateToSearch({ category: id });
  };

  const handleCollectionChange = (id: string | null) => {
    navigateToSearch({ collection: id });
  };

  const handleClearFilters = () => {
    setSearchInput("");
    router.push("/community/search");
  };

  return (
    <>
      <div className="border-b-2 border-retro-line bg-retro-paper pb-8 md:pb-10">
        <CommunityHero />

        <CommunityFilterBar
          searchQuery={searchInput}
          quickFilter={quickFilter}
          selectedRegion={selectedRegion}
          selectedCountry={selectedCountry}
          selectedCategory={selectedCategory}
          selectedCollection={selectedCollection}
          categories={browse.categories}
          collections={browse.collections}
          regions={browse.regions}
          countries={browse.countries}
          onSearchChange={handleSearchChange}
          onQuickFilterChange={handleQuickFilterChange}
          onBasedInChange={handleBasedInChange}
          onCategoryChange={handleCategoryChange}
          onCollectionChange={handleCollectionChange}
          onClearFilters={handleClearFilters}
          showActivePills={true}
        />
      </div>

      <Section
        background="white"
        size="sm"
        className="pt-10 md:pt-12 lg:pt-12"
      >
        <Container size="xl">
          <div className="mb-10 md:mb-12">
            <div>
              <div className="mb-4">
                <p className="text-overline mb-1">Explore by location</p>
                <h2 className="text-h3 text-ink">
                  Find brands close to your market
                </h2>
              </div>
              <div className="space-y-3">
                <RegionFilterChips
                  selected={selectedRegion}
                  onChange={handleRegionChange}
                  regions={browse.regions}
                />
                <CountryFilterChips
                  selected={selectedCountry}
                  onChange={handleCountryChange}
                  countries={browse.countries}
                />
              </div>
            </div>

          </div>

          {/* Featured Collections — product carousels */}
          <div id="collections" className="scroll-mt-24 md:scroll-mt-28">
            {browse.collections.map(({ collection, products }) => (
              <CollectionCarousel
                key={collection.internal_id}
                collection={collection}
                products={products}
              />
            ))}
          </div>

          {/* Categories — stories row */}
          <div id="categories" className="scroll-mt-24 md:scroll-mt-28">
            {browse.categories.length > 0 && (
              <div className="mb-12">
                <h2 className="text-h3 text-ink mb-6">
                  Browse by category
                </h2>
                <CategoryStoriesRow categories={browse.categories} />
              </div>
            )}
          </div>

          {/* Highlighted Suppliers */}
          <HighlightedSuppliersSection
            onSeeAll={() => navigateToSearch({ quick: null, sort: "trending" })}
          />

        </Container>
      </Section>
    </>
  );
}
